import {DateTime} from "luxon";
import Dropdown from "../../../general/Dropdown.tsx";
import {fieldLabelClasses} from "../../../general/fieldStyles.ts";
import {
    formatPeriodLabel,
    isFullYearRange,
} from "../../../general/DateRangeInput/utils/dateRangeUtils.ts";
import type {DateRangeStrings} from "../utils/comparisonPeriods.ts";
import {ComparisonMode, resolveComparison} from "../utils/comparisonPeriods.ts";

const MODES: {mode: ComparisonMode; label: string}[] = [
    {mode: ComparisonMode.None, label: "Geen vergelijking"},
    {mode: ComparisonMode.PreviousPeriod, label: "Vorige periode"},
    {mode: ComparisonMode.SamePeriodLastYear, label: "Zelfde periode vorig jaar"},
];

interface ComparisonPickerProps {
    value: ComparisonMode;
    currentRange: DateRangeStrings;
    onChange: (mode: ComparisonMode) => void;
}

function isFullCalendarYear(range: DateRangeStrings): boolean {
    if (!range.from || !range.to) return false;
    return isFullYearRange(
        DateTime.fromFormat(range.from, "yyyy-MM-dd"),
        DateTime.fromFormat(range.to, "yyyy-MM-dd"),
    );
}

function optionLabel(mode: ComparisonMode, label: string, range: DateRangeStrings): string {
    const resolved = resolveComparison(mode, range);
    if (!resolved) return label;
    return `${label} (${formatPeriodLabel(resolved.from, resolved.to)})`;
}

export function ComparisonPicker({value, currentRange, onChange}: ComparisonPickerProps) {
    const hideSamePeriodLastYear = isFullCalendarYear(currentRange);

    const options = MODES
        .filter(({mode}) => mode !== ComparisonMode.SamePeriodLastYear || !hideSamePeriodLastYear)
        .map(({mode, label}) => ({
            value: mode,
            label: optionLabel(mode, label, currentRange),
        }));

    const selectedValue =
        hideSamePeriodLastYear && value === ComparisonMode.SamePeriodLastYear
            ? ComparisonMode.PreviousPeriod
            : value;
    const selected = options.find((option) => option.value === selectedValue) ?? options[0];

    return (
        <div className="flex flex-col gap-1.5">
            <span className={fieldLabelClasses}>Vergelijk met</span>
            <div className="w-[320px] max-w-[440px]">
                <Dropdown
                    selected={selected}
                    options={options}
                    onUpdateFunc={(mode) => onChange(mode as ComparisonMode)}
                />
            </div>
        </div>
    );
}
